import { useCallback, useEffect, useRef } from 'react';

import { ExternalLinkIcon } from 'lucide-react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { useRules } from '@/modules/rules/hooks/useRules';
import { hasAssignedAiAgent } from '@/modules/rules/util';
import { selectAccountSlug } from '@/redux/session/selectors';

import { useWebchatPreview } from './useWebchatPreview';
import { toTeaserBlock } from '../Accordion/LauncherAppearance/Teaser/serialize';
import { TeaserHostForm } from '../Accordion/LauncherAppearance/Teaser/types';

type Props = {
  /**
   * The absolute URL to render, or `null` when the page row cannot produce one
   * — a path with no website configured, or an empty row. `null` disables the
   * button rather than sending the renderer something broken.
   */
  url: string | null;
  deskId?: string;
  integrationId?: string;
  disabled?: boolean;
};

/**
 * "See the webchat on this page."
 *
 * Sits on a page row rather than in a fieldset of its own, because the URL is
 * already there: the author has said which page this teaser is for, so asking
 * them to retype it somewhere else to look at it is a second source of truth
 * for the same fact.
 *
 * Opens a top-level tab rather than a modal. The preview *is* the widget on a
 * page, and the widget is itself an iframe — so showing it inside a modal
 * nested it one frame deeper than production and changed the geometry it
 * measures. A tab is what a visitor actually gets.
 *
 * What loads there is the author's **unsaved** work: the draft comes straight
 * out of this form through `toTeaserBlock` and the preview document hands it to
 * `MoveoAI.init({ teaser })` as a host-page override, so nothing is written to
 * the integration to look at it.
 */
const PreviewButton = ({ url, deskId, integrationId, disabled }: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const slug = useSelector(selectAccountSlug);
  const { getValues } = useFormContext<TeaserHostForm>();
  const { rules, listStatus: rulesStatus } = useRules(deskId);

  const { start, isPending, previewUrl, errorMessage, reset } = useWebchatPreview(
    deskId,
    integrationId
  );

  /**
   * The tab, opened on the click and filled in when the render lands.
   *
   * It has to be opened *synchronously* inside the handler. A render takes
   * seconds, and by the time it resolves the user gesture has expired, so a
   * `window.open` there is a popup the browser silently blocks — the author
   * would press Preview and watch nothing happen.
   */
  const tabRef = useRef<Window | null>(null);

  const handleClick = useCallback(() => {
    if (!url) {
      return;
    }

    /*
      The same gate the integration header's preview uses. A webchat with no AI
      agent assigned in Rules opens to a silent, unresponsive chat — over the
      author's own page here, which reads as this feature being broken. Gated
      only once rules have loaded, so a slow response cannot raise a false
      warning.
    */
    if (rulesStatus === 'success' && !hasAssignedAiAgent(rules.rules ?? [])) {
      toast.warning(t('integrations.preview_no_agent_title'), {
        description: t('integrations.preview_no_agent_description'),
        action: {
          label: t('common.assign'),
          onClick: () => navigate(`/${slug}/environments/${deskId}/rules`),
        },
      });
      return;
    }

    reset();
    /*
      No `noopener`. It is the safer default almost everywhere, and wrong here:
      `window.open` returns `null` when it is set — that is what the flag means
      — so there is no handle to navigate once the render finishes, and the
      author is left looking at a blank tab for ever.

      Nothing is lost by dropping it. The preview response carries a CSP
      `sandbox`, so the document lands on an opaque origin and cannot reach back
      through `window.opener` into the console. That sandbox is the real
      containment boundary, not this flag.
    */
    const tab = window.open('', '_blank');
    tabRef.current = tab;
    if (!tab) {
      // Blocked by the browser despite being opened inside the click.
      toast.error(t('integrations.web_page_preview.title'), {
        description: t('integrations.web_page_preview.popup_blocked'),
      });
      return;
    }
    // Something has to be there while the render runs, or the author is
    // looking at a blank tab with no idea whether it is working.
    tab.document.write(
      `<!doctype html><title>${t('integrations.web_page_preview.title')}</title>` +
        '<body style="font:14px/1.5 system-ui;margin:0;display:grid;place-items:center;height:100vh;color:#475569">' +
        `<p>${t('integrations.web_page_preview.loading')}</p>`
    );
    tab.document.close();
    start({ url, teaser: toTeaserBlock(getValues('teaser')) });
  }, [deskId, getValues, navigate, reset, rules.rules, rulesStatus, slug, start, t, url]);

  /*
    Hand the finished URL to the tab that is already open.

    An effect rather than the mutation's `onSuccess`, because the tab is
    rendering state that lives outside React: the same URL must not be
    navigated to twice if this component re-renders, which the `previewUrl`
    dependency and clearing the ref together guarantee.
  */
  useEffect(() => {
    const tab = tabRef.current;
    if (!tab || !previewUrl) {
      return;
    }
    tabRef.current = null;
    if (tab.closed) {
      return;
    }
    tab.location.replace(previewUrl);
  }, [previewUrl]);

  /* A failed render leaves a tab showing "preparing" for ever. Close it and say why here. */
  useEffect(() => {
    if (!errorMessage) {
      return;
    }
    const tab = tabRef.current;
    tabRef.current = null;
    if (tab && !tab.closed) {
      tab.close();
    }
    toast.error(t('integrations.web_page_preview.title'), { description: errorMessage });
  }, [errorMessage, t]);

  return (
    <Button
      type="button"
      variant="outline-neutral"
      size="sm"
      disabled={disabled || !url || !deskId || !integrationId || isPending}
      onClick={handleClick}
    >
      {t('integrations.web_page_preview.open')}
      {/*
        Trailing, per the design — the icon says "this opens elsewhere", which
        reads after the label rather than before it.

        Not `isLoading`: it puts the spinner before the label, and here it
        takes the place of the trailing icon.
      */}
      {isPending ? <Spinner className="size-4 text-current" /> : <ExternalLinkIcon size={16} />}
    </Button>
  );
};

export default PreviewButton;
